import { jobActions, contactActions, companyActions } from '../actionTypes';
import { createDidError,
  createRequestSelector,
  createIsLoading,
} from '../requests/requestsSelectors';
import { _trim, _get } from '../utils';
import { selectActiveFilters } from './jobSelectors';
import createLogger from '../../utils/createLogger';

const DEBUG = false;
const debug = createLogger(DEBUG, `jobsActions.js`);


/* -------- selection helpers -------- */
const selectRequest = {
  GET_JOB_FILTERS: (state) => createRequestSelector(`GET_FILTERS`)(state),
};
const selectIsLoading = {
  GET_JOB_POSTINGS: (state) => createIsLoading([`GET_JOB_POSTINGS`])(state),
  GET_JOB_LEADS: (state) => createIsLoading([`GET_JOB_LEADS`])(state),
};
const selectDidError = {
  GET_JOB_POSTINGS: (state) => createDidError([`GET_JOB_POSTINGS`])(state),
  GET_JOB_LEADS: (state) => createDidError([`GET_JOB_LEADS`])(state),
};


/** ---------------- action creators ---------------- */
const getFiltersRequest = () => ({
  type: jobActions.GET_JOB_FILTERS_REQUEST,
});

const getJobPostingsRequest = (filters) => _trim({
  type: jobActions.GET_JOB_POSTINGS_PAGE_REQUEST,
  locations: _get(filters, [`location`, `options`]),
  location: _get(filters, [`location`, `query`]),
  sources: _get(filters, [`source`, `options`]),
});

const getJobLeadsRequest = (filters) => _trim({
  type: jobActions.GET_JOB_LEADS_PAGE_REQUEST,
  filters,
});

const getCompaniesRequest = () => ({
  type: companyActions.GET_COMPANIES_REQUEST,
});

const getCompanyRequest = (id) => ({
  type: companyActions.GET_COMPANY_REQUEST,
  id,
});

const postCompanyRequest = (config) => ({
  type: companyActions.POST_COMPANY_REQUEST,
  ...config,
});

const promoteJobRequest = (id, config) => ({
  type: jobActions.POST_JOB_REQUEST,
  id,
  ...config,
});

const putDuplicateJobRequest = (id, duplicate) => ({
  type: jobActions.PUT_DUPLICATE_JOB_REQUEST,
  id,
  body: { duplicate }
});

const getJobDetailsRequest = (id) => ({
  type: jobActions.GET_JOB_REQUEST,
  id,
});

const getCompanyRecentsRequest = (id) => ({
  type: jobActions.GET_COMPANY_RECENTS_REQUEST,
  id,
});

const submitLeadURLRequest = (id, url) => ({
  type: jobActions.PUT_JOB_REQUEST,
  id,
  url,
});

const changeSearchTermsRequest = (id, search_terms) => ({
  type: jobActions.PUT_JOB_REQUEST,
  id,
  search_terms,
});

const removeJobRequest = (id, status) => ({
  type: jobActions.PUT_JOB_REQUEST,
  id,
  status,
});

const toggleFilterOptionAction = (category_id, option_id) => ({
  type: jobActions.TOGGLE_FILTER_OPTION,
  category_id,
  option_id,
});

const updateFilterQueryAction = (category_id, query) => ({
  type: jobActions.UPDATE_FILTER_QUERY,
  category_id,
  query,
});

const editContactsRequest = (id, contacts) => ({
  type: contactActions.PUT_CONTACTS_REQUEST,
  id,
  body: { contacts },
});

const putCompanyRequest = (id, config) => ({
  type: companyActions.PUT_COMPANY_REQUEST,
  id,
  body: { config },
});

const flushPostingsCacheAction = () => ({
  type: jobActions.FLUSH_POSTINGS_CACHE,
});

const flushLeadsCacheAction = () => ({
  type: jobActions.FLUSH_LEADS_CACHE,
});



/** ---------------- request sequences ---------------- */
/**
 * Retrieves the job filter dictionary, describing
 * supported filters for all job listing requests
 */
const getFilters = () => {
  return (dispatch, getState) => {
    const state = getState();
    const filters = selectRequest.GET_JOB_FILTERS(state);

    debug.enter(`getFilters`);
    debug.log(`  filters`, filters);

    if (filters) return;

    return dispatch(getFiltersRequest())
  }
};


/**
 * Retrieves the next page of open job postings,
 * if available
 */
const getPostings = () => {
  return (dispatch, getState) => {
    const state = getState();
    const isLoading = selectIsLoading.GET_JOB_POSTINGS(state);
    const didError = selectDidError.GET_JOB_POSTINGS(state);
    const filters = selectActiveFilters(state);

    debug.enter(`getJobPostings`);
    debug.log(`  isLoading: ${isLoading}`);
    debug.log(`  didError: ${didError}`);
    debug.log(`  filters`, filters);

    // if last request is pending or failed, halt requests
    if (didError || isLoading) return;

    return dispatch(getJobPostingsRequest(filters));
  }
};

/**
 * Retrieves all job in SQL database to store in redux
 */
const getCompanies = () => {
  return (dispatch, getState) => {
    const state = getState();
    const isLoading = selectIsLoading.GET_JOB_LEADS(state);
    const didError = selectDidError.GET_JOB_LEADS(state);

    debug.enter(`getJobLeads`);
    debug.log(`  isLoading: ${isLoading}`);
    debug.log(`  didError: ${didError}`);

    if (didError || isLoading) return;

    return dispatch(getCompaniesRequest());
  }
};

/**
 * Retrieves all job in SQL database to store in redux
 */
const getCompany = (id) => {
  return (dispatch) => {
    debug.enter(`getCompany`);
    debug.log(`sql company id: ${id}`);
    return dispatch(getCompanyRequest(id));
  }
};

/**
 * Posts new company to SQL
 */
const postCompany = (config) => {
  return (dispatch) => {
    debug.enter(`postCompany`);
    debug.log(`config object: ${config}`);
    return dispatch(postCompanyRequest( {...config}));
  }
};

/**
 * Apply edits to company in SQL
 */
const putCompany = (id, config) => {
  return (dispatch) => {
    debug.enter(`putCompany`);
    debug.log(`config object: ${config}`);
    return dispatch(putCompanyRequest(id, {...config}));
  }
};

/**
 * Retrieves the next page of completed job leads,
 * if available
 */
const getLeads = () => {
  return (dispatch, getState) => {
    const state = getState();
    const isLoading = selectIsLoading.GET_JOB_LEADS(state);
    const didError = selectDidError.GET_JOB_LEADS(state);

    debug.enter(`getJobLeads`);
    debug.log(`  isLoading: ${isLoading}`);
    debug.log(`  didError: ${didError}`);

    // if last request is pending or failed, halt requests
    if (didError || isLoading) return;

    return dispatch(getJobLeadsRequest());
  }
};

/**
 * Retrieves the next page of completed job leads,
 * if available
 */
const getCompanyRecents = (companyId) => {
  return (dispatch) => {
    debug.enter(`getRecentLeads`);
    return dispatch(getCompanyRecentsRequest(companyId));
  }
};

const putDuplicateJob = (id, duplicate) => {
  return (dispatch) => {
    debug.enter(`putDuplicateJob`);
    return dispatch(putDuplicateJobRequest(id, duplicate));
  }
};

/**
 * Sends config object to backend to promote job into lead
 */
const promoteJob = (id, config) => {
  return (dispatch) => {
    debug.enter(`promoteJob`);
    debug.log('promoting job with fields: ' + config);
    return dispatch(promoteJobRequest(id, {
      ...config,
    }));
  }
};

/**
 * Retrieves more details for a specific job given
 * its job id (i.e. description & search results)
 */
const getJobDetails = (id) => {
  return (dispatch) => {
    dispatch(getJobDetailsRequest(id));
  }
};

/**
 * Changes to contact title, name, or LinkedIn page
 */
const editContacts = (id, config) => {
  return (dispatch) => {
    return dispatch(editContactsRequest(id, config))
  }
};

/**
 * Changes job search terms & receive search results
 */
const changeSearchTerms = (id, terms) => {
  return (dispatch) => {
    dispatch(changeSearchTermsRequest(id, terms));
  }
};

/**
 * Adds company lead URL to a job posting, marking
 * it as 'found' and moving data to leads cache
 */
const submitLeadURL = (id, url, callback) => {
  return (dispatch) => {
    dispatch(submitLeadURLRequest(id, url));
    callback();
  }
};

/**
 * Removes a job from the list, marking it as 'closed'
 */
const removeJob = (id, callback) => {
  return (dispatch) => {
    dispatch(removeJobRequest(id, 'closed'));
    if (typeof callback === `function`) {
      callback();
    }
  }
};

/**
 * Removes a job from the list, marking it as 'closed'
 */
const markJob = (id, callback) => {
  return (dispatch) => {
    dispatch(removeJobRequest(id, 'failed'));
    if (typeof callback === `function`) {
      callback();
    }
  }
};

/** ---------------- action sequences ---------------- */
/**
 * Initializes jobs data by first getting
 * the filter spec, then getting postings
 */
const initializeJobData = () => {
  return (dispatch) => {
    debug.enter(`toggleFilterOption`);
    dispatch(getFilters())
      .then(() => dispatch(getPostings()))
      .then(() => dispatch(getLeads()))
  }
};

/**
 * Toggles a filter option for a category
 */
const toggleFilterOption = (category, option) => {
  return (dispatch) => {
    debug.enter(`toggleFilterOption`);
    debug.log(`  category: ${category}`);
    debug.log(`  option: ${option}`);

    dispatch(toggleFilterOptionAction(category, option));
    dispatch(flushPostingsCache());
    dispatch(getPostings());
  }
};

/**
 * Updates a filter category search query
 */
const updateFilterQuery = (category, query) => {
  return (dispatch) => {
    debug.enter(`updateFilterQuery`);
    debug.log(`  category: ${category}`);
    debug.log(`  query: ${query}`);

    dispatch(updateFilterQueryAction(category, query));
    dispatch(flushPostingsCache());
    dispatch(getPostings());
  }
};

const flushPostingsCache = () => {
  return (dispatch) => {
    debug.enter(`flushPostsCache`);
    return dispatch(flushPostingsCacheAction());
  }
};

const flushLeadsCache = () => {
  return (dispatch) => {
    debug.enter(`flushPostsCache`);
    return dispatch(flushLeadsCacheAction());
  }
};

export {
  initializeJobData,
  getJobDetails,
  getPostings,
  getFilters,
  removeJob,
  markJob,
  getLeads,
  getCompanyRecents,
  submitLeadURL,
  changeSearchTerms,
  flushPostingsCache,
  toggleFilterOption,
  updateFilterQuery,
  flushLeadsCache,
  promoteJob,
  putDuplicateJob,
  editContacts,
  getCompanies,
  getCompany,
  postCompany,
  putCompany,
}



// WEBPACK FOOTER //
// ./src/data/jobs/jobActions.js